import { utils } from "web3";
import Web3EthContract from "web3-eth-contract";
import CONFIG from "../config";

/**
 * @name createContract
 * @function
 * @description Starts a new contract 
 * @returns {Object} Contract factory
 */
export const createContract = () => {
    if( window.coeDebug ){
        console.log( 'createContract', 'config', CONFIG.contract );
    }
    Web3EthContract.setProvider( window.ethereum );
    const contract = new Web3EthContract( CONFIG.contract.abi, CONFIG.contract.address );
    if( window.coeDebug ){
        console.log( 'createContract', 'contract', contract.methods );
    }
    return contract;
}

/**
 * @name confirmResponse
 * @function
 * @description Retrieves the status of an offer
 * @param {object} filter
 * @param {number} blockNumber Point to check from
 * @returns 
 */
export const confirmResponse = async ( filter, fromBlock ) => {
    const contract = createContract();
    return await contract.getPastEvents( 'OfferApproved', { filter, fromBlock });
}

/**
 * @name dispatchErr
 * @function
 * @description Handles MetaMask errors
 * @param {object} err 
 * @param {function} dispatch 
 * @param {object} labels 
 */
export const dispatchErr = ( err, enqueueSnackbar, labels={} ) => {
    let message = labels.cantConnect;

    if( err.code === 4001 ){
        message = labels[ err.code ] || err.message;
    }  else if( err.messageKey ){
        message = labels[ err.messageKey ];
    } else {
        message = labels[ err.data?.message ] || err.data?.message || err.message;
    }

    console.error( err, message, labels );

    enqueueSnackbar(
        message,
        { variant: 'error' }
    );
}

/**
 * @name finalizeOffer
 * @function
 * @description Finalizes offer
 * @param {object} offer 
 * @returns {object} Transaction hash
 */
export const finalizeOffer = ({ buyer, seller, tokenId, amount }) => async ( resolve, reject ) => {
    const { selectedAddress } = window.ethereum,
    contract = createContract();
    try {
        const success = contract.methods.finalizeOffer( buyer, seller, tokenId, amount, [] ).send({
            from: selectedAddress,
            value: amount
        });
        resolve( success );
    } catch( err ){
        reject( err );
    }
}

/**
 * @name formatGateway
 * @function
 * @description Formats the IPFS gateway URL
 * @param {string} id 
 * @param {string} file 
 * @returns {string}
 */
export const formatGateway = ( id, file ) => `https://${ id }.ipfs.nftstorage.link/${ file }`;

/**
 * @name getAllTokens
 * @function
 * @description Retrieves all tokens
 */
export const getAllTokens = async () => {

    const contract = createContract(),
    transfers = await contract.getPastEvents( 'Transfer', {
        filter: { _from: '0x0000000000000000000000000000000000000000' },
        fromBlock: 0
    }),
    tokens = [];
    
    transfers.forEach( ({ returnValues }) => {
        const { tokenId } = returnValues;
        if( !tokens.includes( Number( tokenId ) ) ) tokens.push( Number( tokenId ) );
    });

    return tokens;

}

/**
 * @name getBoardFromContract
 * @function
 * @description Retrieves the board data of a specific token
 */
export const getBoardFromContract = async id => {
    const { cid } = await getMetadata( id ),
    uri = formatGateway( cid, 'board.json' ),
    response = await fetch( uri ),
    data = await response.json();
    return data;
}

/**
 * @name getMetadata
 * @function
 * @description Retrieves meta data for a specified token
 * @param {number} id 
 * @returns {object}
 */
export const getMetadata = async id => {
    const factory = createContract(),
    uri = await factory.methods.tokenURI( id ).call(),
    response = await fetch( uri ),
    data = await response.json();
    return data;
}

/**
 * @name getTokenHistory
 * @function
 * @description Get token's history
 * @param {number} tokenId 
 * @returns {array} List of all the token's transactions
 */
export const getTokenHistory = async tokenId => {
    const contract = createContract();
    return await contract.getPastEvents( 'OfferCompleted', { 
        filter: {
            tokenId
        }, 
        fromBlock: 0 
    });
}

/**
 * @name getWalletTokens
 * @function
 * @description Retrieves all tokens contained in a wallet for the current account
 */
export const getWalletTokens = async () => {
    const { selectedAddress } = window.ethereum,
    contract = createContract(),
    balance = await contract.methods.balanceOf( selectedAddress ).call(),
    tokens = [];
    for( let i = 0; i < balance; i++ ){
        const token = await contract.methods.tokenOfOwnerByIndex( selectedAddress, i ).call();
        tokens.push( Number( token ) );
    }
    return tokens.length > 0 ? tokens : null;
}

/**
 * @name offerHistory
 * @function
 * @description Get offer history
 * @param {string} address 
 * @param {string} direction 
 * @param {object} filter 
 * @returns {object}
 */
export const offerHistory = async filter => {
    const contract = createContract(),
    offers = await contract.getPastEvents( 'OfferMade', { filter, fromBlock: 0 }),
    approvals = await contract.getPastEvents( 'OfferApproved', { filter, fromBlock: 0 }),
    rejections = await contract.getPastEvents( 'OfferRejected', { filter, fromBlock: 0 }),
    purchases = await contract.getPastEvents( 'OfferCompleted', { filter, fromBlock: 0 });
    return { offers, approvals, rejections, purchases };
}

/**
 * @name listenForOffers
 * @function 
 * @description Adds a listener for offers
 * @param {function} callback 
 * @returns {array} List of offers up until this point
 */
export const listenForOffers = async ( callback, address, direction, filter ) => {
    const contract = createContract();
    contract.events.OfferMade({ filter, fromBlock: 0 }).on( 'data', offer => callback({ direction, offer, address }) );
}

/**
 * @name listenForPurchases
 * @function
 * @description Listens for purchases
 * @param {function} callback 
 * @param {string} address 
 * @param {string} direction 
 * @param {object} filter 
 */
export const listenForPurchases = async ( callback, address, direction, filter ) => {
    const contract = createContract();
    contract.events.OfferCompleted({ filter, fromBlock: 0 }).on( 'data', offer => callback({ direction, offer, address }) );
}

/**
 * @name listenForResponses
 * @function 
 * @description Adds a listener for offers
 * @param {function} callback 
 * @returns {array} List of offers up until this point
 */
export const listenForResponses = async ( callback, address, direction, filter ) => {
    const contract = createContract();
    contract.events.OfferApproved({ filter, fromBlock: 0 }).on( 'data', offer => callback({ direction, approved: true, offer, address }) );
    contract.events.OfferRejected({ filter, fromBlock: 0 }).on( 'data', offer => callback({ direction, approved: false, offer, address }) );
}

/**
 * @name makeOffer
 * @function
 * @description Makes an offer on an NFT
 * @param {*} resolve 
 * @param {*} reject 
 */
export const makeOffer = ( tokenId, amount ) => async ( resolve, reject ) => {

    const { selectedAddress } = window.ethereum,
    factory = createContract();

    // mint the nft
    try {
        const seller = await factory.methods.ownerOf( tokenId ).call(),
        hash = await factory.methods.makeOffer( selectedAddress, seller, tokenId, amount ).send({ from: selectedAddress });
        resolve( hash );
    } catch( err ){
        reject( err );
    }
         
};

/**
 * @name mintContract
 * @function
 * @description Begins a mint
 * @param {*} resolve 
 * @param {*} reject 
 */
export const mintContract = ({ board, locale }) => async ( resolve, reject ) => {

    const { selectedAddress } = window.ethereum,
    headers = new Headers({ 'Content-Type': 'application/json' }),
    factory = createContract(),
    amount = 1;

    fetch( 
        CONFIG.api + '/metadata', 
        {
            headers,
            method: 'POST',
            mode: 'cors',
            body: JSON.stringify({ board, locale })
        }
    )
        .then( res => res.json().then( data => ({ data, status: res.status, message: res.statusText }) ) )
        .then( ({ data, status, message }) => {

            if( status !== 201 ){
                reject({ status, message });
            } else {

                // pay me
                const { ipnft } = data,
                metadata = formatGateway( ipnft, 'metadata.json' );

                // mint the nft
                factory
                    .methods
                    .mintTo( selectedAddress, metadata )
                    .send({
                        from: selectedAddress,
                        value: utils.toWei( ( 0.05 * amount ).toString(), "ether" ),
                    })
                    .once( 'error', err => {                        
                        reject({ ...err, messageKey: 'notEnoughGas' });
                    })
                    .then( data => { 
                        resolve({  ...data })
                    });
            }
        })
        .catch( reject );    
    

};

/**
 * @name respondToOffer
 * @function
 * @description Responds to an offer
 * @param {number} index 
 * @returns {function}
 */
export const respondToOffer = ( approved, { buyer, seller, tokenId, amount }) => async ( resolve, reject ) => {
    const { selectedAddress } = window.ethereum,
    factory = createContract();

    try {
        const hash = await factory.methods.respondToOffer( buyer, seller, tokenId, amount, approved ).send({ from: selectedAddress });
        resolve( hash );
    } catch( err ){
        reject( err );
    }
}

/**
 * @name validNetwork
 * @function
 * @description Determines if the provided network is valid
 * @param {number} network 
 * @returns {boolean}
 */
export const validNetwork = network => {
    let valid = network.toString() === CONFIG.network;
    return valid;
}